<template>
    <div class="car-replace-container">
        <div class="new-car">
            <QS-h4-title title="新车信息" />
            <el-form ref="formRef" :model="formData" :rules="rules">
                <column-box :labelWidth="160" :column="2">
                    <el-form-item prop="newCarId" :label="'选择待租车辆' + columnSeparator">
                        <CarSelect
                          :modelValue="formData.newCarId"
                          :getCarDetail="true"
                          :getListParams="{ state: 3 }"
                          :extraOptions="carSelectExtraOptions"
                          @update:modelValue="val => { formData.newCarId = val }"
                          @change="handleCarSelect"
                          style="width: 180px"></CarSelect>
                    </el-form-item>

                    <el-form-item prop="carModel">
                        <span>品牌: {{ brandName || '--' }}</span>
                    </el-form-item>
                    <el-form-item prop="carModel" label-width="110px">
                        <span>车系: {{ seriesName ||  '--' }}</span>
                    </el-form-item>
                    <el-form-item prop="carModel">
                        <span>车型: {{ carModelName ?  carModelName : '--' }}</span>
                    </el-form-item>


                    <el-form-item prop="changeReason" :label="'换车原因' + columnSeparator">
                        <el-input
                            type="textarea"
                            :rows="2"
                            show-word-limit
                            maxlength="200"
                            placeholder="请输入换车原因"
                            v-model="formData.changeReason"
                            style="width: 180px"
                        />
                    </el-form-item>

                    <el-form-item
                        v-if="!isEnterprise"
                        prop="signType"
                        :label="'换车协议' + columnSeparator">
                        <el-radio-group v-model="formData.signType" @change="handleSignTypeChange">
                          <el-radio label="2">无需签署</el-radio>
                          <el-radio label="1">需签署</el-radio>
                        </el-radio-group>
                    </el-form-item>

                </column-box>
            </el-form>
        </div>

        <div class="order-info">
            <QS-h4-title title="订单基本信息" />
            <column-box
                :source="orderDetailInfo"
                :list="orderDetailInfoList"
                :labelWidth="130"
            ></column-box>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { getPersonalOrderDetail } from '@/api/domain/orders'
import { getChangeCarRecord } from '@/api/domain/finance'
import { getSimpleCarList, getCarDetailById } from '@/api/carManage/index.js'

const props = defineProps({
  orderId: {
    type: Number,
    default: null,
  },
  isEnterprise: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['sign-type-change'])
const columnSeparator = '：'
const SIGN_TYPE_MAP = {
  NEED: '1',
  NO_NEED: '2',
}

const formRef = ref(null)

const formData = ref({
  newCarId: '',
  changeReason: '',
  signType: props.isEnterprise ? SIGN_TYPE_MAP.NO_NEED : '',
})

const validateSignType = (_rule, value, callback) => {
  if (props.isEnterprise || value) {
    callback()
    return
  }
  callback(new Error('请选择换车协议'))
}

const rules = {
  newCarId: [
    { required: true, message: '请选择车辆', trigger: 'change' },
  ],
  changeReason: [
    { required: true, message: '请填写原因', trigger: 'change' },
  ],
  signType: [
    { validator: validateSignType, trigger: 'change' },
  ],
}

const carModelName = ref('')
const brandName = ref('')
const seriesName = ref('')
const newPlateNo = ref('')

const orderDetailInfo = ref({})

const carSelectExtraOptions = computed(() => {
  if (formData.value.newCarId && newPlateNo.value) {
    return [{ id: formData.value.newCarId, plateNo: newPlateNo.value }]
  }
  return []
})

const orderDetailInfoList = [
  { key: 'id', name: '订单ID' },
  { key: 'plateNo', name: '原车牌号' },
  { key: 'brandName', name: '品牌' },
  { key: 'seriesName', name: '车系' },
  { key: 'modelName', name: '车型' },
  { key: 'customerName', name: '司机姓名' },
  { key: 'tel', name: '联系方式' },
  { key: 'startAndEndDate', name: '合同起止日' },
]

const setSelectedCarInfo = (carInfo = {}) => {
  newPlateNo.value = carInfo?.plateNo || ''
  carModelName.value = carInfo?.modelName || ''
  brandName.value = carInfo?.brandName || ''
  seriesName.value = carInfo?.seriesName || ''
}

const handleCarSelect = (_id, carInfo) => {
  setSelectedCarInfo(carInfo)
}

const handleSignTypeChange = (value) => {
  emit('sign-type-change', value)
}

const getOrderDetailInfo = async () => {
  const { code, data } = await getPersonalOrderDetail({
    orderId: props.orderId,
  })
  if (code === 1) {
    orderDetailInfo.value = {
      ...data,
      startAndEndDate: `${data.startDate}~${data.endDate}`,
    }
  }
}

const resetChangeCarRecord = () => {
  formData.value = {
    newCarId: '',
    changeReason: '',
    signType: props.isEnterprise ? SIGN_TYPE_MAP.NO_NEED : '',
  }
  newPlateNo.value = ''
  setSelectedCarInfo()
  emit('sign-type-change', formData.value.signType)
}

const syncSelectedCarByPlateNo = async (plateNo, recordData = {}) => {
  if (!plateNo) {
    setSelectedCarInfo(recordData)
    formData.value.newCarId = recordData.newCarId || ''
    return
  }

  const { code, data } = await getSimpleCarList({
    plateNo,
    state: 3,
  })

  if (code !== 1 || !Array.isArray(data) || data.length === 0) {
    formData.value.newCarId = recordData.newCarId || ''
    setSelectedCarInfo(recordData)
    return
  }

  const matchedCar = data.find((item) => {
    if (recordData.newCarId) {
      return String(item.id) === String(recordData.newCarId)
    }
    return item.plateNo === plateNo
  }) || data.find((item) => item.plateNo === plateNo)

  if (!matchedCar?.id) {
    formData.value.newCarId = recordData.newCarId || ''
    setSelectedCarInfo(recordData)
    return
  }

  formData.value.newCarId = matchedCar.id

  const detailRes = await Promise.resolve(getCarDetailById({
    carId: matchedCar.id,
  }))

  if (detailRes.code === 1 && detailRes.data) {
    setSelectedCarInfo(detailRes.data)
    return
  }

  setSelectedCarInfo({
    ...recordData,
    plateNo: matchedCar.plateNo || plateNo,
  })
}

const getChangeCarRecordInfo = async () => {
  if (!props.orderId) {
    resetChangeCarRecord()
    return
  }

  const { code, data } = await getChangeCarRecord({
    orderId: props.orderId,
  })

  if (code !== 1 || !data) {
    resetChangeCarRecord()
    return
  }

  formData.value = {
    newCarId: data.newCarId || '',
    changeReason: data.changeReason || '',
    signType: props.isEnterprise
      ? SIGN_TYPE_MAP.NO_NEED
      : (data.signType ? String(data.signType) : ''),
  }
  newPlateNo.value = data.newPlateNo || ''
  await syncSelectedCarByPlateNo(data.newPlateNo, data)
  // syncSelectedCarByPlateNo 可能因车辆不在待租状态而搜不到，确保回显值不丢失
  if (data.newCarId && !formData.value.newCarId) {
    formData.value.newCarId = data.newCarId
  }
  if (data.newPlateNo && !newPlateNo.value) {
    newPlateNo.value = data.newPlateNo
  }
  emit('sign-type-change', formData.value.signType)
}

onMounted(() => {
  getOrderDetailInfo()
  getChangeCarRecordInfo()
})

watch(() => props.orderId, () => {
  getOrderDetailInfo()
  getChangeCarRecordInfo()
})

const getFormData = () => {
  return new Promise((resolve, reject) => {
    formRef.value.validate((valid) => {
      if (valid) {
        resolve(formData.value)
      } else {
        reject(new Error('校验失败'))
      }
    })
  })
}

defineExpose({
  getFormData,
})

</script>

<style lang="scss" scoped>
.car-replace-container {
  padding: 0 18px;
}
</style>
